import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { CalendarCheck, Clock, User, Phone, WhatsappLogo, MapPin, ArrowRight } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function minutesUntil(date, time) {
    if (!date) return null;
    try {
        const dt = new Date(`${date}T${time || '09:00'}:00`);
        return Math.round((dt - new Date()) / 60000);
    } catch {
        return null;
    }
}

function friendlyWhen(mins, date) {
    if (mins === null) return date;
    if (mins < 0) return 'Bezig / zojuist';
    if (mins < 60) return `Over ${mins} min`;
    if (mins < 60 * 24) return `Over ${Math.floor(mins / 60)}u ${mins % 60}m`;
    const days = Math.floor(mins / (60 * 24));
    return days === 1 ? 'Morgen' : `Over ${days} dagen`;
}

export default function NextStudentCard({ students = [], onSelectStudent }) {
    const [nextLesson, setNextLesson] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const res = await axios.get(`${API}/scheduled-lessons`, { withCredentials: true });
                const now = new Date();
                const upcoming = (res.data || [])
                    .map(l => ({ ...l, _dt: new Date(`${l.date}T${l.time || '09:00'}:00`) }))
                    .filter(l => l._dt >= new Date(now.getTime() - 30 * 60000)) // include lessons up to 30 min in the past (current lesson)
                    .sort((a, b) => a._dt - b._dt);
                if (mounted) setNextLesson(upcoming[0] || null);
            } catch (e) {
                console.error('NextStudentCard fetch', e);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        const iv = setInterval(load, 60000); // refresh every minute
        return () => { mounted = false; clearInterval(iv); };
    }, []);

    if (loading) {
        return (
            <Card className="bg-[#2D3748] border-[#4A5568]" data-testid="next-student-card">
                <CardContent className="p-5">
                    <div className="h-16 animate-pulse bg-[#1A202C] rounded" />
                </CardContent>
            </Card>
        );
    }

    if (!nextLesson) {
        return (
            <Card className="bg-[#2D3748] border-[#4A5568]" data-testid="next-student-card-empty">
                <CardContent className="p-5 flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-[#D4A537]/10 border border-[#D4A537]/30 text-[#D4A537]">
                        <CalendarCheck size={22} weight="fill" />
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-gray-500 font-medium">Volgende leerling</div>
                        <div className="text-sm text-gray-300">Geen komende lessen gepland</div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    const student = students.find(s => s.id === nextLesson.student_id);
    const mins = minutesUntil(nextLesson.date, nextLesson.time);
    const when = friendlyWhen(mins, nextLesson.date);
    const isSoon = mins !== null && mins >= 0 && mins <= 90;

    const cleanPhone = (phone) => {
        let p = (phone || '').replace(/[^0-9+]/g, '');
        if (p.startsWith('06')) p = '31' + p.substring(1);
        if (p.startsWith('+')) p = p.substring(1);
        return p;
    };

    return (
        <Card
            className={`bg-[#2D3748] border-2 card-hover ${isSoon ? 'border-[#D4A537]' : 'border-[#4A5568]'}`}
            data-testid="next-student-card"
        >
            <CardContent className="p-5">
                <div className="flex items-start gap-4">
                    <div className={`p-2.5 rounded-lg flex-shrink-0 ${isSoon ? 'bg-[#D4A537] text-[#1A202C]' : 'bg-[#D4A537]/15 text-[#D4A537] border border-[#D4A537]/30'}`}>
                        <CalendarCheck size={24} weight="fill" />
                    </div>

                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div>
                                <div className="text-[11px] uppercase tracking-wider text-gray-500 font-medium">Volgende leerling</div>
                                <div className="text-lg font-semibold text-white truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                    {student?.name || 'Onbekende leerling'}
                                </div>
                            </div>
                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${isSoon ? 'bg-[#D4A537] text-[#1A202C]' : 'bg-[#1A202C] text-[#D4A537] border border-[#D4A537]/30'}`}>
                                {when}
                            </span>
                        </div>

                        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-400">
                            <span className="flex items-center gap-1">
                                <Clock size={14} className="text-[#D4A537]" />
                                {nextLesson.date}{nextLesson.time ? ` • ${nextLesson.time}` : ''}
                            </span>
                            <span className="flex items-center gap-1">
                                <span className="text-gray-500">Duur:</span>
                                <span className="text-white font-medium">{nextLesson.duration_minutes} min</span>
                            </span>
                            {student?.phone && (
                                <span className="flex items-center gap-1">
                                    <Phone size={14} className="text-[#D4A537]" />
                                    <span className="text-white">{student.phone}</span>
                                </span>
                            )}
                            {student?.pickup_address && (
                                <span className="flex items-center gap-1">
                                    <MapPin size={14} className="text-[#D4A537]" />
                                    <span className="text-white">{student.pickup_address}</span>
                                </span>
                            )}
                        </div>

                        {nextLesson.notes && (
                            <div className="mt-2 text-xs text-gray-500 italic">
                                📝 {nextLesson.notes}
                            </div>
                        )}

                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            {student?.phone && (
                                <>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => window.open(`tel:+${cleanPhone(student.phone)}`, '_self')}
                                        className="h-8 border-[#4A5568] text-gray-300 hover:bg-[#3D4A5C]"
                                        data-testid="next-student-call"
                                    >
                                        <Phone size={14} className="mr-1" />
                                        Bellen
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={() => window.open(`https://wa.me/${cleanPhone(student.phone)}`, '_blank')}
                                        className="h-8 bg-green-600 hover:bg-green-500 text-white"
                                        data-testid="next-student-whatsapp"
                                    >
                                        <WhatsappLogo size={14} className="mr-1" weight="fill" />
                                        WhatsApp
                                    </Button>
                                </>
                            )}
                            {student && onSelectStudent && !student.external && (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => onSelectStudent(student)}
                                    className="h-8 text-[#D4A537] hover:bg-[#D4A537]/10 ml-auto"
                                    data-testid="next-student-open"
                                >
                                    <User size={14} className="mr-1" />
                                    Leskaart openen
                                    <ArrowRight size={14} className="ml-1" />
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
